import { useQuery } from "@tanstack/react-query";
import { Location } from "~/db/types";
import {
  getAllMatchingMarkerCoordinates,
  getLocationsWithGeoBounds,
  getNumberOfMatchingLocations,
  getTotalNumberOfLocations,
  MarkerCoordinate,
  searchLocations,
} from "~/lib/server-functions/get-locations";
import { QUERY_KEYS } from "~/lib/tanstack-query/hooks/queryKeys";
import { SearchParams } from "~/types/search";

export type LocationQueryOptions = {
  /**
   * Gate the query.
   *
   * Used to keep the map-only marker query off the wire until the user actually
   * opens the map. Both location queries resolve `location[]` server-side via
   * Place Details or Geocoding, so an unopened map must not trigger a second,
   * unbounded run of that work.
   */
  enabled?: boolean;
};

function boundsToPayload(bounds: google.maps.LatLngBounds) {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  return {
    southWest: { lat: sw.lat(), lng: sw.lng() },
    northEast: { lat: ne.lat(), lng: ne.lng() },
  };
}

export const buildPaginatedLocationsQueryOptions = (
  bounds: google.maps.LatLngBounds | null,
  searchParams: SearchParams,
  page: number,
  pageSize: number = 50,
  options: LocationQueryOptions = {},
) => ({
  queryKey: [
    QUERY_KEYS.LOCATIONS,
    "paginated",
    bounds,
    searchParams,
    page,
    pageSize,
  ],
  queryFn: async () =>
    await getLocationsWithGeoBounds({
      data: {
        bounds: bounds ? boundsToPayload(bounds) : null,
        searchParams,
        page,
        pageSize,
      },
    }),
  enabled: options.enabled ?? true,
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false as const,
  retry: false as const,
});

export const usePaginatedLocations = (
  bounds: google.maps.LatLngBounds | null,
  searchParams: SearchParams,
  page: number,
  pageSize: number = 50,
  options: LocationQueryOptions = {},
) => {
  return useQuery(
    buildPaginatedLocationsQueryOptions(
      bounds,
      searchParams,
      page,
      pageSize,
      options,
    ),
  );
};

export const buildAllMatchingMarkersQueryOptions = (
  bounds: google.maps.LatLngBounds | null,
  searchParams: SearchParams,
  options: LocationQueryOptions = {},
) => ({
  queryKey: [QUERY_KEYS.ALL_MATCHING_MARKERS, bounds, searchParams],
  queryFn: async (): Promise<MarkerCoordinate[]> =>
    await getAllMatchingMarkerCoordinates({
      data: {
        bounds: bounds ? boundsToPayload(bounds) : null,
        searchParams,
      },
    }),
  enabled: options.enabled ?? true,
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false as const,
  retry: false as const,
});

export const useAllMatchingMarkers = (
  bounds: google.maps.LatLngBounds | null,
  searchParams: SearchParams,
  options: LocationQueryOptions = {},
) => {
  return useQuery<MarkerCoordinate[]>(
    buildAllMatchingMarkersQueryOptions(bounds, searchParams, options),
  );
};

export const useSearchLocations = (searchParams: SearchParams) => {
  return useQuery<Location[]>({
    queryKey: [QUERY_KEYS.SEARCH_LOCATIONS, searchParams],
    queryFn: async () => {
      const locations = await searchLocations({
        data: searchParams,
      });

      return locations;
    },
  });
};

export const useNumberOfMatchingLocations = (searchParams: SearchParams) => {
  return useQuery<number>({
    queryKey: [QUERY_KEYS.NUMBER_OF_MATCHING_LOCATIONS, searchParams],
    queryFn: async () => {
      const res = await getNumberOfMatchingLocations({
        data: searchParams,
      });

      return res;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useTotalNumberOfLocations = () => {
  return useQuery<number>({
    queryKey: [QUERY_KEYS.TOTAL_NUMBER_OF_LOCATIONS],
    queryFn: async () => {
      const res = await getTotalNumberOfLocations();

      return res;
    },
  });
};
